import { css } from "@emotion/react";
import { pick } from "es-toolkit/compat";
import { useMemo } from "react";

import type { NodeSelectionCriteria } from "../../library";
import { useIsNodeSelected, useSelectedNodeId } from "../../selection";
import { useGetManifest } from "../../state";
import type { DesignerNode } from "../../types";
import { NodeSlotsEntry } from "./NodeSlotsEntry";
import { NodeTreeEntryActionsMenu } from "./NodeTreeEntryActionsMenu";

const MAX_DEPTH = 6;

interface Props {
  node: DesignerNode;
  depth?: number;
  excludeSelf?: boolean;
}

export const NodeTreeEntry = ({ node, depth = 0, excludeSelf }: Props) => {
  const [_, setSelectedNodeId] = useSelectedNodeId();
  const isSelected = useIsNodeSelected(node.id);
  const getManifest = useGetManifest();

  const children = useMemo(
    () =>
      Object.values(node.childNodes)
        .flatMap((childOrChildren) => (Array.isArray(childOrChildren) ? childOrChildren : [childOrChildren]))
        .filter((child): child is NonNullable<typeof child> => !!child),
    [node.childNodes],
  );

  const manifest = getManifest(node.typeId);
  const designerManifest = manifest.designer;
  const isSelectable = !!(designerManifest.component || designerManifest.components?.properties);

  const availableSlotDescrs: [string, NodeSelectionCriteria][] = Object.entries(manifest.terminus.children ?? {})
    .filter(([name]) => node.childNodes[name] === undefined || Array.isArray(node.childNodes[name]))
    .map(([name, descriptor]) => [name, pick(descriptor, ["role", "typeId", "typeIds"]) as NodeSelectionCriteria]);

  return (
    <div>
      {!excludeSelf && isSelectable && (
        <div
          css={[
            css`
              display: flex;
              flex-direction: row;
              justify-content: space-between;
            `,
            isSelected &&
              css`
                background-color: #eee;
              `,
          ]}
        >
          <p
            css={css`
              margin: 0;
              padding: 0;
            `}
            onClick={() => setSelectedNodeId(node.id)}
            title={`Node ${node.id} (${node.typeId})`}
          >
            {node.title}
          </p>
          <NodeTreeEntryActionsMenu node={node} />
        </div>
      )}

      {children.length > 0 && depth < MAX_DEPTH && (
        <ul
          key="children"
          css={css`
            padding-left: 1.5em;
          `}
        >
          {children.map((child) => (
            <NodeTreeEntry key={child.id} node={child} depth={depth + 1} />
          ))}
        </ul>
      )}

      {availableSlotDescrs.length > 0 && (
        <ul
          key="slots"
          css={css`
            padding-left: 1.5em;
          `}
        >
          {availableSlotDescrs.map(([name, descriptor]) => (
            <NodeSlotsEntry key={name} parentNode={node} name={name} descriptor={descriptor} />
          ))}
        </ul>
      )}
    </div>
  );
};
